/**
 * Curated "fun fact" copy for a subset of elements that make good
 * Element-of-the-Day picks. The picker (see `engine.ts`) only chooses
 * from the keys of this record so visitors never get a dud day with
 * an obscure synthetic element that has no story to tell.
 *
 * Keep facts to ~1 sentence, classroom-safe, and verifiable.
 */
export interface ElementFact {
  /** Headline fact shown on the card. */
  fact: string;
  /** Optional second sentence with a deeper "did you know?" angle. */
  didYouKnow?: string;
}

export const ELEMENT_FACTS: Record<number, ElementFact> = {
  1: {
    fact: "Hydrogen makes up about 75% of all normal matter in the universe by mass.",
    didYouKnow: "Stars fuse hydrogen into helium - that reaction is what powers our Sun.",
  },
  2: {
    fact: "Helium was discovered in the Sun's spectrum before it was found on Earth.",
    didYouKnow: "Its name comes from 'helios', the Greek word for Sun.",
  },
  3: {
    fact: "Lithium is so light it floats on oil and is used in rechargeable batteries.",
    didYouKnow: "It is the lightest metal on the periodic table.",
  },
  6: {
    fact: "Carbon forms more compounds than any other element - the basis of all known life.",
    didYouKnow: "Diamond and graphite are both pure carbon, just arranged differently.",
  },
  7: {
    fact: "About 78% of Earth's atmosphere is nitrogen gas.",
    didYouKnow: "Liquid nitrogen boils at -196 degC and is used to freeze biological samples.",
  },
  8: {
    fact: "Oxygen was independently discovered by Joseph Priestley and Carl Scheele in the 1770s.",
    didYouKnow: "It is the most abundant element in Earth's crust by mass.",
  },
  9: {
    fact: "Fluorine is the most electronegative element - it pulls electrons harder than any other.",
    didYouKnow: "It is so reactive it can set water on fire.",
  },
  10: {
    fact: "Neon glows orange-red when an electric current passes through it.",
    didYouKnow: "Other 'neon' sign colors actually come from different noble gases.",
  },
  11: {
    fact: "Sodium is so reactive it must be stored under oil to keep it away from air and water.",
    didYouKnow: "Drop it in water and it produces hydrogen gas - sometimes with a flame.",
  },
  12: {
    fact: "Magnesium burns with a brilliant white light and was once used in camera flashes.",
    didYouKnow: "Chlorophyll, the molecule that makes plants green, is built around magnesium.",
  },
  13: {
    fact: "Aluminium was once more valuable than gold - until cheap electrolysis was invented in 1886.",
    didYouKnow: "The cap of the Washington Monument is solid aluminium for that reason.",
  },
  14: {
    fact: "Silicon is the second most abundant element in Earth's crust, after oxygen.",
    didYouKnow: "It is the backbone of every microchip in your devices.",
  },
  16: {
    fact: "Sulfur has been known since ancient times and was called 'brimstone' in old texts.",
    didYouKnow: "The smell of struck matches and rotten eggs both come from sulfur compounds.",
  },
  17: {
    fact: "Chlorine gas is yellow-green and was the first chemical weapon used in WWI.",
    didYouKnow: "Tiny amounts disinfect drinking water and swimming pools safely.",
  },
  19: {
    fact: "Potassium reacts so violently with water it can ignite the hydrogen it produces.",
    didYouKnow: "It is essential for nerve signals - bananas are a famous source.",
  },
  20: {
    fact: "Calcium is the most abundant metal in the human body - mostly in bones and teeth.",
    didYouKnow: "Pure calcium metal is silvery and tarnishes quickly in air.",
  },
  26: {
    fact: "Iron is what makes your blood red - it sits at the center of haemoglobin.",
    didYouKnow: "Earth's core is mostly iron, which is why we have a magnetic field.",
  },
  29: {
    fact: "Copper has been used for over 10,000 years - one of the first metals humans worked.",
    didYouKnow: "The Statue of Liberty is copper; it turned green from oxidation.",
  },
  30: {
    fact: "Zinc is what makes a galvanised nail rust-proof - it sacrifices itself first.",
    didYouKnow: "Brass is mostly copper and zinc.",
  },
  35: {
    fact: "Bromine is one of only two elements that is liquid at room temperature.",
    didYouKnow: "Its name comes from 'bromos', Greek for 'stench'.",
  },
  47: {
    fact: "Silver is the best electrical conductor of any element - even better than copper.",
    didYouKnow: "It also kills bacteria, which is why it is used in some bandages.",
  },
  53: {
    fact: "Iodine sublimates - it goes straight from solid to purple vapour without melting.",
    didYouKnow: "Your thyroid needs iodine to make hormones, which is why salt is iodised.",
  },
  74: {
    fact: "Tungsten has the highest melting point of any pure metal: 3422 degC.",
    didYouKnow: "That is why old incandescent bulbs used tungsten filaments.",
  },
  78: {
    fact: "Platinum is so unreactive it is found in nature as the pure metal.",
    didYouKnow: "It is the catalyst inside every car's catalytic converter.",
  },
  79: {
    fact: "All the gold ever mined would fit in a cube about 22 metres on each side.",
    didYouKnow: "Gold is so unreactive that ancient gold artifacts still look brand new.",
  },
  80: {
    fact: "Mercury is the only metal that is liquid at room temperature.",
    didYouKnow: "It used to fill thermometers but is now banned in most because it is toxic.",
  },
  82: {
    fact: "Lead is so dense that a small brick of it weighs more than you expect.",
    didYouKnow: "The Romans used lead pipes - and may have suffered chronic lead poisoning.",
  },
  86: {
    fact: "Radon is a radioactive noble gas that seeps from rocks into basements.",
    didYouKnow: "Despite being a noble gas, it is the second leading cause of lung cancer.",
  },
  92: {
    fact: "Uranium is named after the planet Uranus, discovered just 8 years before the element.",
    didYouKnow: "A single uranium-235 fission releases 50 million times more energy than burning a carbon atom.",
  },
  94: {
    fact: "Plutonium is so radioactive it feels warm to the touch.",
    didYouKnow: "The first nuclear reactor designed to make it filled an entire stadium.",
  },
};

/** Atomic numbers eligible to be Element of the Day. */
export const DAILY_POOL: number[] = Object.keys(ELEMENT_FACTS)
  .map((k) => Number(k))
  .sort((a, b) => a - b);